import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { analyseOperation, analyseTexte } from "@/lib/analyse";
import OperationAnalysisPanel from "@/components/OperationAnalysisPanel";
import QualificationPreview from "@/components/QualificationPreview";
import QualityGatePanel from "@/components/QualityGatePanel";
import PreparationPanel from "@/components/PreparationPanel";
import EngineInsight from "@/components/EngineInsight";
import { Check, Save, Sparkles, ArrowRight, ArrowLeft, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const KEY = "pc_op_draft_v2";
const STEPS = ["Contexte opération", "Entreprises intervenantes", "Zones et planning", "Analyse des risques",
  "Coactivité", "Permis et autorisations", "Plan de prévention", "Validation et signature"];
const FONCTIONS = ["Responsable maintenance", "Chargé d'affaires", "Chef de projet",
  "Conducteur de travaux", "Responsable immobilier", "Responsable HSE"];

const DEFAULT = {
  siteId: "", organisation: "Groupe Immobilier Atlantique", direction: "Direction Immobilier",
  responsableOperation: "", fonctionResponsable: "", intitule: "", description: "",
  siteOccupe: false, presencePublicOuCollaborateurs: false, entrepriseExterieure: true,
  sousTraitance: false, recurrence: "ponctuels", horaires: "jour",
  eeId: "", sousTraitants: [], zones: [], natureTravaux: [], dateDebut: "", dateFin: "", signature: false,
  typeOperation: "reelle",
  qualChargement: null, qualChantierBTP: null, qualPlusieurs: null, qualVolume: "",
};

function TriChip({ value, onChange, testid }) {
  const opts = [[true, "Oui"], [false, "Non"], [null, "Non renseigné"]];
  return (
    <div className="chips" data-testid={testid}>
      {opts.map(([v, l]) => (
        <button type="button" key={String(v)} className={`chip ${value === v ? "on" : ""}`} onClick={() => onChange(v)}>{l}</button>
      ))}
    </div>
  );
}

function YesNo({ value, onChange, testid }) {
  return (
    <div className="chips" data-testid={testid}>
      <button type="button" className={`chip ${value ? "on" : ""}`} onClick={() => onChange(true)}>Oui</button>
      <button type="button" className={`chip ${!value ? "on" : ""}`} onClick={() => onChange(false)}>Non</button>
    </div>
  );
}

export default function OperationNew() {
  const { sites, entreprises, zones, labels } = useApp();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [step, setStep] = useState(0);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState([]);
  const [gates, setGates] = useState(null);
  const [synthese, setSynthese] = useState(null);
  const [moteur, setMoteur] = useState(null);
  const [anomalies, setAnomalies] = useState([]);
  // Brique 3 « 5 premières minutes » : préremplissage depuis les données maîtres.
  const [prepItems, setPrepItems] = useState([]);
  const [prepRepris, setPrepRepris] = useState(0);
  const [origin, setOrigin] = useState({});        // field -> { source, fiabilite } (badge d'origine)
  const autoKeys = useRef(new Set());               // clés préremplies non encore modifiées manuellement
  const [autoResult, setAutoResult] = useState(null); // synthèse explicable de l'analyse d'intervention
  const WKEY = "pc_op_wizard_state_v1";
  const [validatedSteps, setValidatedSteps] = useState(() => { try { return JSON.parse(localStorage.getItem(WKEY))?.validated || []; } catch { return []; } });
  const [toReview, setToReview] = useState(() => { try { return JSON.parse(localStorage.getItem(WKEY))?.review || []; } catch { return []; } });
  const [reviewReason, setReviewReason] = useState(() => { try { return JSON.parse(localStorage.getItem(WKEY))?.reviewReason || ""; } catch { return ""; } });
  const validatedRef = useRef(validatedSteps);
  useEffect(() => { validatedRef.current = validatedSteps; }, [validatedSteps]);
  useEffect(() => { localStorage.setItem(WKEY, JSON.stringify({ validated: validatedSteps, review: toReview, reviewReason })); }, [validatedSteps, toReview, reviewReason]);
  const [form, setForm] = useState(() => {
    const d = localStorage.getItem(KEY);
    return d ? { ...DEFAULT, ...JSON.parse(d) } : DEFAULT;
  });

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(form));
    setSaved(true);
    const t = setTimeout(() => setSaved(false), 1200);
    return () => clearTimeout(t);
  }, [form]);

  useEffect(() => {
    if (sp.get("type") === "formation") setForm((f) => ({ ...f, typeOperation: "formation" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Un changement de donnée impactante fait repasser les étapes AVAL déjà validées à « À revoir ».
  const IMPACT = {
    siteId: { steps: [1, 2, 3, 4, 5, 6], reason: "Le site d'intervention a été modifié. Les étapes suivantes doivent être vérifiées à nouveau." },
    zones: { steps: [3, 4, 5, 6], reason: "La zone d'intervention a été modifiée. L'analyse de coactivité et les mesures de prévention doivent être vérifiées à nouveau." },
    natureTravaux: { steps: [3, 4, 5, 6], reason: "La nature des travaux a été modifiée. L'analyse des risques et les permis recommandés doivent être vérifiés à nouveau." },
    dateDebut: { steps: [4], reason: "Les dates ont été modifiées. L'analyse de coactivité doit être vérifiée à nouveau." },
    dateFin: { steps: [4], reason: "Les dates ont été modifiées. L'analyse de coactivité doit être vérifiée à nouveau." },
    eeId: { steps: [3, 4, 5, 6], reason: "L'entreprise extérieure a été modifiée. Les étapes suivantes doivent être vérifiées à nouveau." },
    sousTraitants: { steps: [3, 4, 5, 6], reason: "Les intervenants ont été modifiés. Les étapes suivantes doivent être vérifiées à nouveau." },
  };
  const applyImpact = (key) => {
    const rule = IMPACT[key];
    if (!rule) return;
    const hit = validatedRef.current.filter((s) => rule.steps.includes(s));
    if (hit.length) {
      setValidatedSteps((vs) => vs.filter((s) => !hit.includes(s)));
      setToReview((tr) => [...new Set([...tr, ...hit])]);
      setReviewReason(rule.reason);
    }
  };
  const up = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    // Une modification manuelle « démarque » l'origine préremplie (devient saisie utilisateur).
    if (autoKeys.current.has(k)) {
      autoKeys.current.delete(k);
      setOrigin((o) => { const n = { ...o }; delete n[k]; return n; });
    }
    applyImpact(k);
  };
  const toggle = (k, v) => { setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] })); applyImpact(k); };

  const focusField = (fieldId) => {
    if (!fieldId) return;
    const el = document.querySelector(`[data-testid="${fieldId}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    const inp = el.matches("input,select,textarea") ? el : el.querySelector("input,select,textarea");
    try { inp?.focus(); } catch { /* noop */ }
    el.classList.add("field-flash");
    setTimeout(() => el.classList.remove("field-flash"), 1800);
  };
  const siteZones = useMemo(() => zones.filter((z) => z.siteId === form.siteId), [zones, form.siteId]);
  const selectedZones = useMemo(() => zones.filter((z) => form.zones.includes(z.id)), [zones, form.zones]);
  const ee = entreprises.filter((e) => e.type === "exterieure");
  const analysis = useMemo(() => analyseOperation(form, selectedZones, labels), [form, selectedZones, labels]);

  const autoAnalyse = () => {
    const { travauxSuggeres, notes } = analyseTexte(`${form.intitule} ${form.description}`);
    const nouveaux = travauxSuggeres.filter((t) => !form.natureTravaux.includes(t));
    const detecteSiteOccupe = !form.siteOccupe && /occup|présence|usager|public/i.test(form.description);
    const detecteST = !form.sousTraitance && /sous-trait|sous trait/i.test(form.description);
    setForm((f) => ({
      ...f,
      natureTravaux: [...new Set([...f.natureTravaux, ...travauxSuggeres])],
      siteOccupe: f.siteOccupe || detecteSiteOccupe,
      sousTraitance: f.sousTraitance || detecteST,
    }));
    setAutoResult({
      travaux: travauxSuggeres.map((t) => (labels?.travaux?.[t] || t)),
      nbNouveaux: nouveaux.length,
      siteOccupe: detecteSiteOccupe, sousTraitance: detecteST,
      notes: notes.slice(0, 4),
      vide: travauxSuggeres.length === 0 && !detecteSiteOccupe && !detecteST,
    });
    toast.success(travauxSuggeres.length ? `${travauxSuggeres.length} type(s) de travaux détecté(s)` : "Aucun mot-clé de risque détecté");
  };

  const REQUIRED = {
    0: [["siteId", "Site concerné"], ["organisation", "Organisation"], ["responsableOperation", "Responsable de l'opération"], ["fonctionResponsable", "Fonction du responsable"], ["intitule", "Intitulé de l'opération"]],
    1: form.entrepriseExterieure ? [["eeId", "Entreprise extérieure"]] : [],
    2: [["dateDebut", "Date de début"], ["dateFin", "Date de fin"], ["zones", "Zones concernées"], ["natureTravaux", "Nature des travaux"]],
    7: [["signature", "Validation / signature"]],
  };
  const missingFor = (s) => (REQUIRED[s] || []).filter(([k]) => { const v = form[k]; return Array.isArray(v) ? !v.length : !v; }).map(([, l]) => l);

  // --- Quality Gates : évaluation serveur (débounced) sur le brouillon courant ---
  useEffect(() => {
    const payload = {
      typeOperation: form.typeOperation, siteId: form.siteId, organisation: form.organisation,
      responsableOperation: form.responsableOperation, fonctionResponsable: form.fonctionResponsable,
      intitule: form.intitule, description: form.description,
      entrepriseExterieure: form.entrepriseExterieure, eeId: form.eeId,
      sousTraitance: form.sousTraitance, sousTraitants: form.sousTraitants,
      dateDebut: form.dateDebut, dateFin: form.dateFin, zones: form.zones, natureTravaux: form.natureTravaux,
      signature: form.signature, qualChargement: form.qualChargement,
      qualChantierBTP: form.qualChantierBTP, qualPlusieurs: form.qualPlusieurs,
    };
    const t = setTimeout(() => {
      api.post("/quality-gates/evaluate", payload)
        .then((r) => { setGates(r.data.gates); setSynthese(r.data.synthese); setMoteur(r.data.moteur); setAnomalies(r.data.anomalies || []); })
        .catch(() => { /* repli sur la validation locale */ });
    }, 400);
    return () => clearTimeout(t);
  }, [form]);

  const currentGate = gates ? gates.find((g) => g.index === step) : null;
  const canNext = currentGate ? currentGate.bloquants.length === 0 : missingFor(step).length === 0;

  // --- Brique 3 : préremplissage depuis données maîtres (débounced sur site/EE/zones) ---
  useEffect(() => {
    const zoneIds = (form.zones || []).join(",");
    if (!form.siteId && !form.eeId && !zoneIds) { setPrepItems([]); setPrepRepris(0); return; }
    const t = setTimeout(() => {
      api.get("/operations/prefill", { params: { siteId: form.siteId, eeId: form.entrepriseExterieure ? form.eeId : "", zoneIds } })
        .then((r) => {
          setPrepItems(r.data.items || []);
          setPrepRepris(r.data.repris || 0);
          const pf = r.data.prefill || {};
          const srcByField = {};
          (r.data.items || []).forEach((it) => { if (it.field) srcByField[it.field] = { source: it.source, fiabilite: it.fiabilite }; });
          setForm((f) => {
            const nf = { ...f };
            const applyKey = (k, v, badgeField) => {
              if (v === undefined || v === null || v === "") return;
              const cur = (f[k] ?? "").toString().trim();
              // On préremplit si le champ est vide, encore à sa valeur par défaut, ou déjà auto-rempli.
              if (!cur || cur === (DEFAULT[k] ?? "").toString().trim() || autoKeys.current.has(k)) {
                nf[k] = v; autoKeys.current.add(k);
                if (badgeField) setOrigin((o) => ({ ...o, [k]: srcByField[badgeField] || { source: "Organisation", fiabilite: "verifier" } }));
              }
            };
            applyKey("organisation", pf.organisation, "op-organisation");
            applyKey("direction", pf.direction, null);
            applyKey("responsableOperation", pf.responsableOperation, "op-responsable");
            if (pf.fonctionResponsable && FONCTIONS.includes(pf.fonctionResponsable)) applyKey("fonctionResponsable", pf.fonctionResponsable, null);
            return nf;
          });
        })
        .catch(() => { /* non bloquant : la préparation reste facultative */ });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.siteId, form.eeId, form.entrepriseExterieure, form.zones]);

  // Compteur « 5 premières minutes » : uniquement les champs obligatoires de CRÉATION (hors signature).
  const creationFields = [
    { key: "intitule", label: "Intitulé", step: 0, field: "op-titre", ok: !!(form.intitule || "").trim() },
    { key: "responsableOperation", label: "Responsable d'opération", step: 0, field: "op-responsable", ok: !!(form.responsableOperation || "").trim() },
    { key: "dateDebut", label: "Date de début", step: 2, field: "op-debut", ok: !!form.dateDebut },
    { key: "dateFin", label: "Date de fin", step: 2, field: "op-fin", ok: !!form.dateFin },
    { key: "horaires", label: "Horaires", step: 2, field: null, ok: !!form.horaires },
    { key: "zones", label: "Zone(s)", step: 2, field: "field-zones", ok: (form.zones || []).length > 0 },
    { key: "natureTravaux", label: "Nature des travaux", step: 2, field: "field-travaux", ok: (form.natureTravaux || []).length > 0 },
    ...(form.entrepriseExterieure ? [{ key: "eeId", label: "Entreprise extérieure", step: 1, field: "op-ee", ok: !!form.eeId }] : []),
  ];
  const aCompleter = creationFields.filter((c) => !c.ok);
  const OriginHint = ({ k }) => origin[k] ? (
    <div data-testid={`origin-${k}`} style={{ fontSize: ".7rem", marginTop: 3, color: origin[k].fiabilite === "verifier" ? "var(--ambre, #B26A00)" : "var(--vert)" }}>
      Prérempli depuis {origin[k].source}{origin[k].fiabilite === "verifier" ? " · à vérifier" : ""}
    </div>
  ) : null;

  const validateAndContinue = () => {
    if (!canNext) {
      setMissing(currentGate ? currentGate.bloquants.map((m) => m.label) : missingFor(step));
      const first = currentGate?.bloquants?.[0];
      if (first?.field) focusField(first.field);
      return;
    }
    setMissing([]);
    setValidatedSteps((vs) => [...new Set([...vs, step])]);
    setToReview((tr) => tr.filter((s) => s !== step));
    setReviewReason("");
    setStep((s) => Math.min(7, s + 1));
  };
  const next = validateAndContinue;
  const saveDraft = () => { localStorage.setItem(KEY, JSON.stringify(form)); toast.success("Brouillon enregistré", { description: "Vous pourrez reprendre cette opération plus tard." }); };

  const submit = async () => {
    if (!canNext) { setMissing(currentGate ? currentGate.bloquants.map((m) => m.label) : missingFor(7)); return; }
    setBusy(true);
    try {
      const payload = {
        siteId: form.siteId, donneurOrdre: [form.organisation, form.direction].filter(Boolean).join(" — ") || form.organisation,
        eeId: form.entrepriseExterieure ? form.eeId : "", sousTraitants: form.sousTraitance ? form.sousTraitants : [],
        zones: form.zones, natureTravaux: form.natureTravaux, criticite: analysis.criticite.niveau,
        dateDebut: form.dateDebut, dateFin: form.dateFin, titre: form.intitule, description: form.description, statut: "planifiee",
        organisation: form.organisation, direction: form.direction, responsableOperation: form.responsableOperation,
        fonctionResponsable: form.fonctionResponsable, siteOccupe: form.siteOccupe,
        presencePublicOuCollaborateurs: form.presencePublicOuCollaborateurs, entrepriseExterieure: form.entrepriseExterieure,
        sousTraitance: form.sousTraitance, recurrence: form.recurrence, horaires: form.horaires,
        facteursCriticite: analysis.criticite.facteurs, criticiteJustification: analysis.criticite.justification,
        typeOperation: form.typeOperation || "reelle",
      };
      const { data } = await api.post("/operations", payload);
      // Persiste les critères de qualification saisis dans le wizard (relance le calcul serveur)
      try {
        await api.post(`/operations/${data.id}/qualification`, {
          schemaVersion: 1, entrepriseExterieurePresente: form.entrepriseExterieure,
          operationChargementDechargement: form.qualChargement, chantierBatimentGenieCivil: form.qualChantierBTP,
          plusieursEntreprises: form.qualPlusieurs,
          volumeHorairePrevisionnel: form.qualVolume === "" ? null : Number(form.qualVolume),
          siteEnActivite: form.siteOccupe, sousTraitance: form.sousTraitance,
          periodeMois: null, effectifPrevisionnel: null, risquesInterference: null,
          natureOperation: null, commentairesQualification: null,
        });
      } catch (e) { /* la qualification initiale existe déjà, non bloquant */ }
      localStorage.removeItem(KEY);
      localStorage.removeItem(WKEY);
      toast.success(`Opération ${data.id} créée`);
      nav(`/operations/${data.id}`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const Info = ({ children }) => <div className="notice" style={{ background: "var(--vert-p)", borderColor: "var(--vert)" }}>{children}</div>;

  return (
    <div>
      <div className="page-head">
        <div><div className="crumb">Parcours de prévention · Nouvelle opération</div><h1>Nouvelle opération</h1></div>
        <div className="autosave">{saved ? <><Check size={12} />Brouillon enregistré</> : <><Save size={12} />Sauvegarde auto</>}</div>
      </div>

      <div style={{ marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span className="mono" style={{ fontSize: ".7rem", color: "var(--text-muted)", textTransform: "uppercase" }} data-testid="step-indicator">Étape {step + 1} sur 8 · {STEPS[step]}</span>
      </div>
      <div style={{ height: 6, background: "var(--ligne)", borderRadius: 4, overflow: "hidden", marginBottom: 18 }}>
        <div style={{ height: "100%", width: `${((step + 1) / 8) * 100}%`, background: "var(--sarcelle)", transition: "width .2s" }} />
      </div>
      <div className="step-mobile" data-testid="wizard-step-mobile">
        <div className="sm-top"><span>Étape {step + 1} sur 8</span><span>{Math.round(((step + 1) / 8) * 100)}%</span></div>
        <div className="sm-title">{STEPS[step]}</div>
        <div className="sm-bar"><i style={{ width: `${((step + 1) / 8) * 100}%` }} /></div>
      </div>
      <div className="steps steps-wizard" style={{ marginBottom: 18 }}>
        {STEPS.map((s, i) => (
          <div key={s} className={`step ${i === step ? "on" : ""} ${i < step ? "done" : ""}`} style={{ cursor: "pointer", minWidth: 90 }} onClick={() => setStep(i)}>
            <span className="num">{i < step ? "✓" : i + 1}</span>{s}
          </div>
        ))}
      </div>

      <div className="wizard-grid">
        <div className="card">
          {step === 0 && (
            <div data-testid="op-step-0">
              <label className="f">Type d'opération</label>
              <div style={{ display: "flex", gap: 18, marginBottom: 6 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: ".9rem" }} data-testid="op-type-reelle">
                  <input type="radio" name="typeOperation" checked={form.typeOperation !== "formation"}
                    onChange={() => up("typeOperation", "reelle")} /> Réelle
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: ".9rem" }} data-testid="op-type-formation">
                  <input type="radio" name="typeOperation" checked={form.typeOperation === "formation"}
                    onChange={() => up("typeOperation", "formation")} /> Formation
                </label>
              </div>
              {form.typeOperation === "formation" && (
                <div className="notice" data-testid="op-formation-info"
                  style={{ background: "var(--ambre-p, #FDF3E0)", borderColor: "var(--ambre, #E49B1A)", marginBottom: 14 }}>
                  Cette opération est créée dans un contexte pédagogique. Les signatures sont simulées.
                  Les notifications externes sont désactivées. Les documents générés portent un filigrane FORMATION
                  et n'ont aucune valeur opérationnelle.
                </div>
              )}
              <div className="row2">
                <div><label className="f">Site concerné</label>
                  <select data-testid="op-site" value={form.siteId} onChange={(e) => up("siteId", e.target.value)}>
                    <option value="">— Sélectionner —</option>
                    {sites.map((s) => <option key={s.id} value={s.id}>{s.nom} · {s.ville}</option>)}
                  </select></div>
                <div><label className="f">Organisation / donneur d'ordre</label>
                  <input data-testid="op-organisation" value={form.organisation} onChange={(e) => up("organisation", e.target.value)} />
                  <OriginHint k="organisation" /></div>
              </div>
              <div className="row2">
                <div><label className="f">Direction / service concerné</label>
                  <input value={form.direction} onChange={(e) => up("direction", e.target.value)} /></div>
                <div><label className="f">Responsable de l'opération</label>
                  <input data-testid="op-responsable" value={form.responsableOperation} onChange={(e) => up("responsableOperation", e.target.value)} placeholder="Nom Prénom" />
                  <OriginHint k="responsableOperation" /></div>
              </div>
              <label className="f">Fonction du responsable</label>
              <select data-testid="op-fonction" value={form.fonctionResponsable} onChange={(e) => up("fonctionResponsable", e.target.value)}>
                <option value="">— Sélectionner —</option>
                {FONCTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
              <label className="f">Intitulé de l'opération</label>
              <input data-testid="op-titre" value={form.intitule} onChange={(e) => up("intitule", e.target.value)} placeholder="Ex : Réfection étanchéité toiture bâtiment B" />
              <label className="f">Description / contexte</label>
              <textarea data-testid="op-desc" rows={3} value={form.description} onChange={(e) => up("description", e.target.value)} placeholder="Décrivez les travaux : le moteur détecte les risques et permis à partir du texte." />
              <button type="button" className="btn btn-a btn-sm" style={{ marginTop: 8 }} onClick={autoAnalyse} data-testid="op-auto-analyse"><Sparkles size={14} />Analyser l'intervention</button>
              {autoResult && (
                <div className="notice" data-testid="op-auto-result" style={{ marginTop: 10, background: "var(--sarcelle-p, #E5F0F0)", borderColor: "var(--sarcelle)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <Sparkles size={14} color="var(--sarcelle)" />
                    <b style={{ fontSize: ".86rem" }}>Ce que le moteur a identifié dans votre description</b>
                  </div>
                  {autoResult.vide ? (
                    <p style={{ fontSize: ".82rem", margin: 0 }}>Aucun mot-clé de risque reconnu. Complétez la description ou sélectionnez la nature des travaux à l'étape « Zones et planning ».</p>
                  ) : (
                    <ul style={{ margin: "0 0 6px", paddingLeft: 18, fontSize: ".82rem", lineHeight: 1.6 }}>
                      {autoResult.travaux.length > 0 && <li>Types de travaux : {autoResult.travaux.join(", ")}{autoResult.nbNouveaux > 0 ? ` (${autoResult.nbNouveaux} ajouté${autoResult.nbNouveaux > 1 ? "s" : ""} à l'étape « Zones et planning »)` : ""}.</li>}
                      {autoResult.siteOccupe && <li>Indice de site occupé pendant les travaux → coché.</li>}
                      {autoResult.sousTraitance && <li>Indice de sous-traitance → coché.</li>}
                      {autoResult.notes.map((n, i) => <li key={i}>{n}</li>)}
                    </ul>
                  )}
                  <div style={{ fontSize: ".76rem", color: "var(--text-muted)", display: "flex", gap: 6, alignItems: "flex-start" }}>
                    <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 2 }} color="var(--ambre, #B26A00)" />
                    <span>Propositions <b>indicatives à vérifier</b> : détectées par mots-clés, jamais conclues automatiquement. Rien n'est validé tant que vous ne confirmez pas chaque étape.</span>
                  </div>
                </div>
              )}

              <div className="row2" style={{ marginTop: 14 }}>
                <div><label className="f">Site occupé pendant les travaux</label><YesNo testid="op-site-occupe" value={form.siteOccupe} onChange={(v) => up("siteOccupe", v)} /></div>
                <div><label className="f">Présence de public / collaborateurs</label><YesNo testid="op-public" value={form.presencePublicOuCollaborateurs} onChange={(v) => up("presencePublicOuCollaborateurs", v)} /></div>
              </div>
              <div className="row2">
                <div><label className="f">Entreprise extérieure</label><YesNo testid="op-ee-toggle" value={form.entrepriseExterieure} onChange={(v) => up("entrepriseExterieure", v)} /></div>
                <div><label className="f">Sous-traitance prévue</label><YesNo testid="op-st-toggle" value={form.sousTraitance} onChange={(v) => up("sousTraitance", v)} /></div>
              </div>
              <label className="f">Travaux récurrents ou ponctuels</label>
              <div className="chips">
                {[["recurrents", "Récurrents"], ["ponctuels", "Ponctuels"]].map(([k, v]) => (
                  <button type="button" key={k} className={`chip ${form.recurrence === k ? "on" : ""}`} onClick={() => up("recurrence", k)}>{v}</button>
                ))}
              </div>

              <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--ligne)" }}>
                <h3 style={{ fontSize: ".92rem", margin: "0 0 4px" }}>Qualification du régime applicable</h3>
                <p style={{ fontSize: ".78rem", color: "var(--text-muted)", margin: "0 0 10px" }}>
                  Ces éléments déterminent le régime (PDP écrit, protocole de sécurité, coordination SPS…). La carte de droite évolue en direct.
                </p>
                <div className="row2">
                  <div><label className="f">Opération de chargement / déchargement</label><TriChip testid="op-qual-chargement" value={form.qualChargement} onChange={(v) => up("qualChargement", v)} /></div>
                  <div><label className="f">Chantier de bâtiment / génie civil</label><TriChip testid="op-qual-btp" value={form.qualChantierBTP} onChange={(v) => up("qualChantierBTP", v)} /></div>
                </div>
                <div className="row2">
                  <div><label className="f">Plusieurs entreprises présentes</label><TriChip testid="op-qual-plusieurs" value={form.qualPlusieurs} onChange={(v) => up("qualPlusieurs", v)} /></div>
                  <div><label className="f">Volume horaire prévisionnel cumulé (h / 12 mois)</label>
                    <input type="number" data-testid="op-qual-volume" value={form.qualVolume} onChange={(e) => up("qualVolume", e.target.value)} placeholder="Ex : 520" /></div>
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="op-step-1">
              {form.entrepriseExterieure ? <>
                <label className="f">Entreprise extérieure principale</label>
                <select data-testid="op-ee" value={form.eeId} onChange={(e) => up("eeId", e.target.value)}>
                  <option value="">— Sélectionner —</option>
                  {ee.map((e) => <option key={e.id} value={e.id}>{e.nom}</option>)}
                </select>
                {form.sousTraitance && <>
                  <label className="f">Sous-traitants</label>
                  <div className="chips" data-testid="field-soustraitants">
                    {ee.filter((e) => e.id !== form.eeId).map((e) => (
                      <button type="button" key={e.id} className={`chip ${form.sousTraitants.includes(e.id) ? "on" : ""}`} onClick={() => toggle("sousTraitants", e.id)}>{e.nom}</button>
                    ))}
                  </div>
                </>}
              </> : <Info>Travaux réalisés en régie interne — pas d'entreprise extérieure. Le plan de prévention reste recommandé si coactivité.</Info>}
            </div>
          )}

          {step === 2 && (
            <div data-testid="op-step-2">
              <div className="row3">
                <div><label className="f">Date de début</label><input data-testid="op-debut" type="date" value={form.dateDebut} onChange={(e) => up("dateDebut", e.target.value)} /></div>
                <div><label className="f">Date de fin</label><input data-testid="op-fin" type="date" value={form.dateFin} onChange={(e) => up("dateFin", e.target.value)} /></div>
                <div><label className="f">Horaires</label>
                  <select value={form.horaires} onChange={(e) => up("horaires", e.target.value)}><option value="jour">Jour</option><option value="nuit">Nuit</option><option value="continu">Continu</option></select></div>
              </div>
              <label className="f">Zones concernées {form.siteId ? "" : "(sélectionnez d'abord un site)"}</label>
              <div className="chips" data-testid="field-zones">
                {siteZones.map((z) => (
                  <button type="button" key={z.id} className={`chip ${form.zones.includes(z.id) ? "on" : ""}`} onClick={() => toggle("zones", z.id)} data-testid={`op-zone-${z.id}`}>{z.nom}</button>
                ))}
                {!siteZones.length && <span className="empty" style={{ padding: 0 }}>Aucune zone pour ce site.</span>}
              </div>
              <label className="f">Nature des travaux</label>
              <div className="chips" data-testid="field-travaux">
                {Object.entries(labels.travaux).map(([k, v]) => (
                  <button type="button" key={k} className={`chip ${form.natureTravaux.includes(k) ? "on" : ""}`} onClick={() => toggle("natureTravaux", k)} data-testid={`op-travail-${k}`}>{v}</button>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="op-step-3">
              <h3 style={{ fontSize: "1rem", marginBottom: 8 }}>Analyse des risques (automatique)</h3>
              <p style={{ fontSize: ".84rem", color: "var(--text-muted)", marginBottom: 10 }}>Risques déduits des travaux et des attributs de zone. Ils seront confirmés et complétés lors de l'inspection commune préalable.</p>
              {analysis.risques.length ? <ul style={{ paddingLeft: 18, fontSize: ".86rem", lineHeight: 1.6 }}>{analysis.risques.map((r, i) => <li key={i}>{r}</li>)}</ul> : <div className="empty" style={{ textAlign: "left", padding: 0 }}>Sélectionnez des travaux/zones à l'étape précédente.</div>}
            </div>
          )}

          {step === 4 && (
            <div data-testid="op-step-4">
              <h3 style={{ fontSize: "1rem", marginBottom: 8 }}>Coactivité</h3>
              {analysis.conflits.length ? analysis.conflits.map((c, i) => (
                <div className="conf" key={i} style={{ marginBottom: 8 }}><div className="d">{c}</div></div>
              )) : <Info>Aucun conflit de coactivité potentiel détecté à ce stade. L'analyse fine sera recalculée par le moteur lors des autorisations de travail.</Info>}
              {analysis.vigilance.length > 0 && <><label className="f">Points de vigilance</label><ul style={{ paddingLeft: 18, fontSize: ".84rem", lineHeight: 1.6 }}>{analysis.vigilance.map((v, i) => <li key={i}>{v}</li>)}</ul></>}
            </div>
          )}

          {step === 5 && (
            <div data-testid="op-step-5">
              <h3 style={{ fontSize: "1rem", marginBottom: 8 }}>Permis et autorisations</h3>
              <p style={{ fontSize: ".84rem", color: "var(--text-muted)", marginBottom: 10 }}>Permis probablement requis. Les autorisations de travail correspondantes seront créées et évaluées par le moteur après la création de l'opération.</p>
              {analysis.permisRequis.length ? <div className="chips">{analysis.permisRequis.map((p) => <span key={p.code} className="tag">{p.label}</span>)}</div> : <div className="empty" style={{ textAlign: "left", padding: 0 }}>Aucun permis spécifique requis d'après les travaux saisis.</div>}
            </div>
          )}

          {step === 6 && (
            <div data-testid="op-step-6">
              <h3 style={{ fontSize: "1rem", marginBottom: 8 }}>Plan de prévention</h3>
              <Info>Le plan de prévention sera généré automatiquement après la création de l'opération, à partir du contexte, de l'inspection commune et du moteur de risques/coactivité. Export PDF et page imprimable disponibles depuis la fiche opération.</Info>
            </div>
          )}

          {step === 7 && (
            <div data-testid="op-step-7">
              <h3 style={{ fontSize: "1rem", marginBottom: 8 }}>Validation et signature</h3>
              <table className="kv" style={{ marginBottom: 12 }}><tbody>
                <tr><td>Intitulé</td><td><b>{form.intitule || "—"}</b></td></tr>
                <tr><td>Site</td><td>{sites.find((s) => s.id === form.siteId)?.nom || "—"}</td></tr>
                <tr><td>Responsable</td><td>{form.responsableOperation} {form.fonctionResponsable && `(${form.fonctionResponsable})`}</td></tr>
                <tr><td>Période</td><td className="mono">{form.dateDebut || "?"} → {form.dateFin || "?"}</td></tr>
                <tr><td>Criticité calculée</td><td><span className={`badge b-${analysis.criticite.niveau}`}>{analysis.criticite.justification.split("(")[0]}</span></td></tr>
                <tr><td>Statut d'analyse</td><td>{analysis.statut === "bloquant" ? "Bloquant" : analysis.statut === "sous_conditions" ? "Compatible sous conditions" : "Compatible"}</td></tr>
              </tbody></table>
              <label className="ck"><input type="checkbox" checked={form.signature} data-testid="op-signature" onChange={(e) => up("signature", e.target.checked)} />
                <span>Je valide la création de cette opération et lance le parcours de prévention (inspection commune → plan de prévention → autorisations).</span></label>
            </div>
          )}

          {currentGate && (() => {
            const bloquants = currentGate.controls.filter((c) => c.niveau === "bloquant");
            const avertissements = currentGate.controls.filter((c) => c.niveau === "avertissement");
            const recos = currentGate.controls.filter((c) => c.niveau === "recommandation");
            const recoCount = currentGate.avertissements.length + currentGate.recommandationsList.length;
            const inReview = toReview.includes(step);
            return (
              <div className="qg-step-checks" data-testid="qg-step-checks" style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--ligne)" }}>
                {inReview && reviewReason && (
                  <div className="notice qg-review" data-testid="qg-review-banner" style={{ marginBottom: 10 }}>
                    <b>À revoir.</b> {reviewReason}
                  </div>
                )}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4, flexWrap: "wrap", gap: 6 }}>
                  <span className="f" style={{ margin: 0 }}>Vérifications avant validation</span>
                  <span className={`badge b-${currentGate.statut === "conforme" ? "faible" : "elevee"}`} data-testid="qg-step-status">
                    {currentGate.statut === "conforme" ? "✅ Étape conforme" : "❌ Étape incomplète"}
                  </span>
                </div>
                {currentGate.statut === "conforme" && recoCount > 0 && (
                  <div className="qg-reco-hint" data-testid="qg-reco-hint">{recoCount} recommandation{recoCount > 1 ? "s" : ""} disponible{recoCount > 1 ? "s" : ""} ci-dessous</div>
                )}

                {bloquants.length > 0 && (<>
                  <div className="qg-cat">Obligatoire — conditionne la validation</div>
                  <ul className="qg-checks">
                    {bloquants.map((ctrl) => (
                      <li key={ctrl.id} className={ctrl.passed ? "ok" : "ko clickable"} data-testid={`qg-check-${ctrl.id}`}
                        onClick={() => !ctrl.passed && focusField(ctrl.field)} title={!ctrl.passed ? "Cliquez pour corriger" : ""}>
                        {ctrl.passed ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                        <span>{ctrl.label}</span>
                      </li>
                    ))}
                  </ul>
                </>)}

                {avertissements.length > 0 && (<>
                  <div className="qg-cat avert">Avertissements — à vérifier, non bloquants</div>
                  <ul className="qg-checks">
                    {avertissements.map((ctrl) => (
                      <li key={ctrl.id} className={ctrl.passed ? "ok" : "warn"} data-testid={`qg-check-${ctrl.id}`}>
                        {ctrl.passed ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
                        <span>{ctrl.label}{!ctrl.passed && ctrl.message ? <em className="qg-opt"> — {ctrl.message}</em> : null}</span>
                      </li>
                    ))}
                  </ul>
                </>)}

                {recos.length > 0 && (<>
                  <div className="qg-cat reco">Recommandations du moteur métier — informatives</div>
                  <ul className="qg-checks">
                    {recos.map((ctrl) => (
                      <li key={ctrl.id} className={ctrl.passed ? "ok" : "warn"} data-testid={`qg-check-${ctrl.id}`}>
                        {ctrl.passed ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
                        <span>{ctrl.label}{!ctrl.passed && ctrl.message ? <em className="qg-opt"> — {ctrl.message}</em> : null}</span>
                      </li>
                    ))}
                  </ul>
                </>)}
              </div>
            );
          })()}

          {[3, 4, 5, 6].includes(step) && moteur && <EngineInsight moteur={moteur} />}

          {step === 7 && (
            <div className={`qg-final ${synthese?.pretACreer ? "ok" : "ko"}`} data-testid="qg-final-recap" style={{ marginTop: 16 }}>
              {synthese?.pretACreer ? (
                <div className="notice" style={{ background: "var(--vert-p)", borderColor: "var(--vert)" }}>
                  <b>✅ Le dossier est complet et prêt à être créé.</b> Toutes les étapes obligatoires sont conformes.
                </div>
              ) : (
                <div data-testid="qg-final-anomalies">
                  <div className="notice" style={{ background: "var(--rouge-p)", borderColor: "var(--rouge)" }}>
                    <b>{anomalies.length} point{anomalies.length > 1 ? "s" : ""} bloquant{anomalies.length > 1 ? "s" : ""}</b> à corriger avant de créer le dossier.
                  </div>
                  <ul className="qg-anomalies">
                    {anomalies.map((a, i) => (
                      <li key={i} data-testid={`qg-anomaly-${a.id}`}>
                        <div>
                          <b>{a.label}</b>
                          <span className="qg-anom-etape">Étape concernée : {a.etape}</span>
                          {a.message ? <span className="qg-anom-msg">{a.message}</span> : null}
                        </div>
                        <button className="btn btn-p btn-sm" data-testid={`qg-fix-${a.id}`}
                          onClick={() => { setMissing([]); setStep(a.index); setTimeout(() => focusField(a.field), 400); }}>Corriger maintenant<ArrowRight size={13} /></button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {!canNext && missing.length > 0 && step !== 7 && (
            <div className="notice" style={{ background: "var(--rouge-p)", borderColor: "var(--rouge)", marginTop: 14 }} data-testid="op-missing">
              <b>Cette étape n'est pas encore complète.</b>{" "}
              {(currentGate ? currentGate.bloquants.length : missing.length)} information{(currentGate ? currentGate.bloquants.length : missing.length) > 1 ? "s doivent être renseignées" : " doit être renseignée"} avant de poursuivre :
              <ul className="qg-missing-list" style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                {(currentGate ? currentGate.bloquants : missing.map((l) => ({ label: l, message: "", field: null }))).map((m, i) => (
                  <li key={i} className={m.field ? "clickable" : ""} onClick={() => m.field && focusField(m.field)} title={m.field ? "Cliquez pour localiser le champ" : ""}>
                    {m.label}{m.message ? <span style={{ color: "var(--text-muted)" }}> — {m.message}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "space-between", flexWrap: "wrap" }}>
            <button className="btn btn-g" onClick={() => { setMissing([]); setStep((s) => Math.max(0, s - 1)); }} disabled={step === 0}><ArrowLeft size={14} />Précédent</button>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn btn-g" data-testid="op-save-draft" onClick={saveDraft}><Save size={14} />Enregistrer comme brouillon</button>
              {step < 7
                ? <button className={`btn ${canNext ? "btn-p" : "btn-g"}`} data-testid="op-next" onClick={next} title={canNext ? "Valider et poursuivre" : "Complétez les contrôles obligatoires"}>Valider et poursuivre<ArrowRight size={14} /></button>
                : <button className="btn btn-p" data-testid="op-submit" onClick={submit} disabled={busy || !canNext}>{busy ? "Création…" : "Créer l'opération"}</button>}
            </div>
          </div>
        </div>

        <div>
          <PreparationPanel items={prepItems} repris={prepRepris} aCompleter={aCompleter}
            onGoTo={(i, field) => { setMissing([]); setStep(i); if (field) setTimeout(() => focusField(field), 300); }} />
          <QualityGatePanel gates={gates} currentStep={step} synthese={synthese} validatedSteps={validatedSteps} toReview={toReview} onGoTo={(i) => { setMissing([]); setStep(i); }} />
          <QualificationPreview form={form} />
          <OperationAnalysisPanel analysis={analysis} />
        </div>
      </div>
    </div>
  );
}
